import { useState } from "react";
import { globalVariables } from "../../../../global/GlobalVariables";
import { Button, Grid, Link, Typography } from "@mui/material";
import { postNotification } from "../../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import {
  getData,
  postData,
} from "../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { StripeIcon } from "../../../../components/icons/payment/StripeIcon";
import { getLanguageCookie } from "../../../../language/LanguageFunctions";
import { useTranslation } from "react-i18next";

const IS_BCP = getEnvironmentVariable("IS_BCP") === "true";
const BETA_STRIPE_LINKING =
  getEnvironmentVariable("BETA_STRIPE_LINKING") === "true";

export default function BankDetailsTab(props) {
  const { user, authContext } = props;
  const [processing, setProcessing] = useState(false);
  const globalVars = globalVariables();

  const isAdmin = authContext?.authState?.identity?.isAdmin || false;

  // Language setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.account.bankDetails"
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h3" fontWeight="700" gutterBottom>
            {t("wantToSellTickets", {
              defaultValue: "Want to sell tickets to your events?"
            })}
          </Typography>

          {IS_BCP ? (
            <Typography variant="body1" paragraph>
              {globalVars?.site?.title}{" "}
              {t("p1a", {
                defaultValue:
                  "provides Event Organizers with a  simple way to register and process payments for events!"
              })}
            </Typography>
          ) : (
            <Typography variant="body1" paragraph>
              {globalVars?.site?.title}{" "}
              {t("p1b", {
                defaultValue:
                  "has partnered with Best Coast Pairings to provide Event Organizers with a simple way to register and process payments for events!"
              })}
            </Typography>
          )}
          <Typography variant="body1" paragraph>
            {t("p2", {
              defaultValue:
                "This powerful tool is powered by the secure and trusted"
            })}
            <Link
              href="https://stripe.com/"
              target="_Blank"
              underline="none"
              sx={{ color: "link.stripe" }}
            >
              {t("stripe", {
                defaultValue: " Stripe"
              })}
            </Link>{" "}
            {isAdmin && (
              <>
                {t("and", {
                  defaultValue: " and"
                })}
                <Link
                  href="https://squareup.com/us/en"
                  target="_Blank"
                  underline="none"
                  sx={{ color: "link.square" }}
                >
                  {t("square", {
                    defaultValue: " Square"
                  })}
                </Link>
              </>
            )}
            {t(isAdmin ? "p3b" : "p3a", {
              defaultValue: isAdmin
                ? " payment platforms."
                : " payment platform."
            })}
            {t("p4", {
              defaultValue:
                " Event Organizers can attach a Business Account, Debit Card or personal checking account to their"
            })}
            {" " + getEnvironmentVariable("APP_NAME") || "BCP"}
            {t("p5", {
              defaultValue:
                " account and once enabling online registration within Event Details, can set the price for 2-3 days of purchase. You can find more information on our"
            })}
            <Link href="/pricing" target="_Blank" underline="none" color="link">
              {t("p6", {
                defaultValue: " Pricing"
              })}
            </Link>{" "}
            {t("p7", {
              defaultValue: " page."
            })}
          </Typography>
        </Grid>

        {/* Payment Processor Buttons */}

        {/* Stripe */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "link.stripe",
              color: "#000",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "#fff"
              }
            }}
            startIcon={<StripeIcon />}
            onClick={async () => {
              if (BETA_STRIPE_LINKING) {
                if (user?.bankAccount?.id) {
                  setProcessing(true);
                  try {
                    const returnUrl = `${window.location.origin}/stripe/account-creation`;
                    const refreshUrl = `${window.location.origin}/account?active_tab=bank-details`;

                    const link = await getData(
                      `/bankaccounts/stripe/${user.bankAccount.id}/url`,
                      {
                        return_url: returnUrl,
                        refresh_url: refreshUrl,
                      }
                    );

                    if (typeof link === "string" && link.startsWith("http")) {
                      window.location.assign(link);
                    } else {
                      throw new Error("Invalid URL received from server");
                    }
                  } catch (err) {
                    postNotification("error", err);
                  }
                  setProcessing(false);
                } else {
                  setProcessing(true);
                  try {
                    const bankAccount = await postData("/bankaccounts/stripe");

                    const returnUrl = `${window.location.origin}/stripe/account-creation`;
                    const refreshUrl = `${window.location.origin}/account?active_tab=bank-details`;

                    const urlResponse = await getData(
                      `/bankaccounts/stripe/${bankAccount.id}/url`,
                      {
                        return_url: returnUrl,
                        refresh_url: refreshUrl,
                      }
                    );

                    if (
                      typeof urlResponse === "string" &&
                      urlResponse.startsWith("http")
                    ) {
                      window.location.assign(urlResponse);
                    } else {
                      throw new Error("Invalid URL received from server");
                    }
                  } catch (err) {
                    postNotification("error", err);
                    setProcessing(false);
                  }
                }
              } else {
                if (user?.bankAccount?.id) {
                  setProcessing(true);
                  try {
                    const link = await getData(
                      `/stripe/expresslink/${user.bankAccount.id}`
                    );

                    window.open(link.url, "popup");
                  } catch (err) {
                    postNotification("error", err);
                  }
                  setProcessing(false);
                } else {
                  var addParams = `?redirect_uri=${window.location.origin}/stripe/account-creation`;
                  if (user.email) {
                    addParams = addParams + "&stripe_user[email]=" + user.email;
                  }
                  addParams =
                    addParams + "&stripe_user[first_name]=" + user.firstName;
                  addParams =
                    addParams + "&stripe_user[last_name]=" + user.lastName;
                  addParams =
                    addParams +
                    `&state=${user.id}&client_id=${getEnvironmentVariable(
                      "STRIPE_CLIENT_ID"
                    )}`;

                  window.location.assign(
                    "https://connect.stripe.com/express/oauth/authorize" +
                      addParams
                  );
                }
              }
            }}
          >
            {t(
              user?.bankAccount?.id
                ? "updateStripeAccount"
                : "linkStripeAccount",
              {
                defaultValue: user?.bankAccount?.id
                  ? "Update Stripe Bank Account"
                  : "Link Bank Account with Stripe",
                keyPrefix: "buttons"
              }
            )}
          </Button>
        </Grid>
      </Grid>

      {Boolean(processing) && <ModalLoadingFull />}
    </>
  );
}
